import { create } from 'zustand';
import type { SizeChartDto, SizeChartRequest } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * Size charts (ADR-072): tables written once and hung on categories and
 * products in their own editors. Searched and paged on the server.
 */

interface SizeChartsState {
  items: SizeChartDto[];
  nextCursor: string | null;
  total: number | null;
  search: string;
  loading: boolean;
  saving: boolean;
  error: string | null;
  saveError: string | null;

  load: (search?: string) => Promise<void>;
  loadMore: () => Promise<void>;
  save: (id: string | null, input: SizeChartRequest) => Promise<boolean>;
  remove: (id: string) => Promise<boolean>;
  clearSaveError: () => void;
}

const PAGE_SIZE = 20;

export const useSizeChartsStore = create<SizeChartsState>((set, get) => ({
  items: [],
  nextCursor: null,
  total: null,
  search: '',
  loading: false,
  saving: false,
  error: null,
  saveError: null,

  async load(search) {
    const term = search ?? get().search;
    set({ loading: true, error: null, search: term });
    try {
      const page = await withSession(() => adminApi.sizeCharts.list({ limit: PAGE_SIZE, ...(term.trim() ? { search: term.trim() } : {}) }));
      if (get().search !== term) return;
      set({ items: page.items, nextCursor: page.meta.nextCursor, total: page.meta.total ?? null, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the size charts.') });
    }
  },

  async loadMore() {
    const { nextCursor, search, loading } = get();
    if (!nextCursor || loading) return;
    set({ loading: true });
    try {
      const page = await withSession(() =>
        adminApi.sizeCharts.list({ limit: PAGE_SIZE, cursor: nextCursor, ...(search.trim() ? { search: search.trim() } : {}) }),
      );
      set((state) => ({ items: [...state.items, ...page.items], nextCursor: page.meta.nextCursor, loading: false }));
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load more size charts.') });
    }
  },

  async save(id, input) {
    set({ saving: true, saveError: null });
    try {
      const saved = await withSession(() => (id ? adminApi.sizeCharts.update(id, input) : adminApi.sizeCharts.create(input)));
      set((state) => ({
        saving: false,
        items: id ? state.items.map((item) => (item.id === id ? saved : item)) : [saved, ...state.items],
        total: id || state.total === null ? state.total : state.total + 1,
      }));
      return true;
    } catch (error) {
      set({ saving: false, saveError: errorMessage(error, 'Could not save the size chart.') });
      return false;
    }
  },

  async remove(id) {
    set({ saving: true, error: null });
    try {
      await withSession(() => adminApi.sizeCharts.remove(id));
      set((state) => ({
        saving: false,
        items: state.items.filter((item) => item.id !== id),
        total: state.total === null ? null : state.total - 1,
      }));
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not delete the size chart.') });
      return false;
    }
  },

  clearSaveError() {
    set({ saveError: null });
  },
}));
